import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function AcceptInvite() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Invalid or missing invite token');
      return;
    }

    if (!password || !confirmPassword) {
      setError('Please enter a password');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch('/api/auth/accept-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to accept invitation');
      }

      setSuccess(true);
      setTimeout(() => {
        // Redirect to login page
        navigate('/auth?mode=login', { replace: true });
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#0F1F3D',
        color: '#F5F8FC',
        padding: '20px',
      }}>
        <div style={{ maxWidth: '400px', textAlign: 'center' }}>
          <h1 style={{ marginBottom: '20px' }}>Invalid Invitation Link</h1>
          <p style={{ marginBottom: '20px', color: '#7BAED4' }}>
            The invitation link is missing or invalid. Please check the link and try again.
          </p>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '10px 20px',
              background: '#00D4FF',
              color: '#0A0E1A',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: '#0F1F3D',
      color: '#F5F8FC',
      padding: '20px',
    }}>
      <div style={{
        maxWidth: '420px',
        width: '100%',
        background: 'rgba(15, 31, 61, 0.9)',
        border: '1px solid rgba(74, 127, 165, 0.2)',
        borderRadius: '12px',
        padding: '40px',
      }}>
        <h1 style={{
          fontSize: '24px',
          fontWeight: '600',
          marginBottom: '12px',
          textAlign: 'center',
        }}>
          Welcome to CrossRegGuard
        </h1>

        <p style={{
          color: '#7BAED4',
          fontSize: '14px',
          textAlign: 'center',
          marginBottom: '32px',
          lineHeight: '1.6',
        }}>
          You've been invited to join the team. Set your password to get started.
        </p>

        {error && (
          <div style={{
            background: 'rgba(255, 100, 100, 0.1)',
            border: '1px solid rgba(255, 100, 100, 0.3)',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '20px',
            color: '#FF6464',
            fontSize: '13px',
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            background: 'rgba(100, 200, 100, 0.1)',
            border: '1px solid rgba(100, 200, 100, 0.3)',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '20px',
            color: '#64C864',
            fontSize: '13px',
            textAlign: 'center',
          }}>
            ✓ Account created! Redirecting to login...
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{
              display: 'block',
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '0.08em',
              color: '#7BAED4',
              marginBottom: '8px',
              textTransform: 'uppercase',
            }}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password (min 8 characters)"
              disabled={loading || success}
              style={{
                width: '100%',
                padding: '12px 14px',
                background: '#0A1A30',
                border: '1px solid rgba(74, 127, 165, 0.3)',
                borderRadius: '8px',
                color: '#F5F8FC',
                fontSize: '14px',
                fontFamily: "'DM Mono', monospace",
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '0.08em',
              color: '#7BAED4',
              marginBottom: '8px',
              textTransform: 'uppercase',
            }}>
              Confirm Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm password"
              disabled={loading || success}
              style={{
                width: '100%',
                padding: '12px 14px',
                background: '#0A1A30',
                border: '1px solid rgba(74, 127, 165, 0.3)',
                borderRadius: '8px',
                color: '#F5F8FC',
                fontSize: '14px',
                fontFamily: "'DM Mono', monospace",
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <button
            type="submit"
            disabled={loading || success}
            style={{
              padding: '12px 20px',
              background: loading || success ? 'rgba(74, 127, 165, 0.3)' : 'linear-gradient(135deg, #00D4FF, #0099BB)',
              color: '#0A0E1A',
              border: 'none',
              borderRadius: '6px',
              fontWeight: '700',
              fontSize: '13px',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              cursor: loading || success ? 'default' : 'pointer',
              transition: 'all 0.2s',
              marginTop: '8px',
            }}
          >
            {loading ? 'Creating Account...' : success ? 'Account Created! ✓' : 'Create Account & Join Team'}
          </button>
        </form>

        <p style={{
          fontSize: '12px',
          color: '#4A7FA5',
          textAlign: 'center',
          marginTop: '24px',
        }}>
          After creating your account, you'll be able to log in to CrossRegGuard and access the training platform.
        </p>
      </div>
    </div>
  );
}
